/**
 * 路径封装类.
 *
 * @author  yusangeng@outlook.com
 */
var wildcard = "*";
var doubleWildcard = "**";
var isString = function (v) { return typeof v === "string"; };
/**
 * 路径封装类.
 *
 * 路径是指形如`a/b/c/d`的字符串, 其中`/`隔开的部分叫做分段, 分段可以为字符串、通配符`*`, 或者多分段通配符`**`
 * 注意, 在一个分段内, 不支持通配符和和其他字符混用.
 *
 * 相当于简陋版的minimatch(https://github.com/isaacs/minimatch).
 *
 * 可以通过赋值Logger.injector.Path替换.
 *
 * @export
 * @class Path
 */
var Path = /** @class */ (function () {
    /**
     * Creates an instance of Path.
     *
     * @param {string|Path} str 路径字符串或其他路径对象, 不可为空字符串
     *
     * @memberof Path
     * @private
     */
    function Path(str) {
        this.str = "";
        var pathStr = (this.str = (str || "").toString());
        this.sections_ = pathStr
            .split(/\/+/)
            .map(function (el) { return el.trim(); })
            .filter(function (el) { return el.length; });
        if (!this.sections_.length) {
            console.warn("\u4E0D\u5EFA\u8BAE\u4F7F\u7528\u7A7A\u5B57\u7B26\u4E32(\"".concat(pathStr, "\")\u6784\u9020Path\u5B9E\u4F8B."));
        }
    }
    Object.defineProperty(Path.prototype, "sections", {
        /**
         * 路径分段列表
         *
         * @readonly
         *
         * @memberof Path
         */
        get: function () {
            return this.sections_.slice();
        },
        enumerable: false,
        configurable: true
    });
    /**
     * 转化为字符串
     *
     * @returns {string} 转化结果
     *
     * @memberof Path
     * @instance
     */
    Path.prototype.toString = function () {
        return this.str;
    };
    /**
     * 是否相等
     *
     * @param {string | Path} other 路径字符串或其他路径对象
     * @returns {boolean} 相等返回true, 否则返回false
     *
     * @memberof Path
     * @instance
     */
    Path.prototype.equal = function (other) {
        return this.toString() === other.toString();
    };
    /**
     * 是否匹配
     *
     * `a/#/b`可以匹配`a/foobar/b`, `a/##/b`可以匹配`a/foobar/b`、`a/foo/bar/b`以及`a/b`, `##`可以匹配任意路径(#号实际为*号)
     *
     * @param {string | Path} other 路径字符串或其他路径对象, 不可包含通配符
     * @returns {boolean} 匹配返回true, 否则返回false
     *
     * @memberof Path
     * @instance
     */
    Path.prototype.match = function (other) {
        var otherPath = isString(other) ? new Path(other) : other;
        if (other.toString().indexOf(wildcard) !== -1) {
            throw new Error("被匹配的路径不可包含通配符(*或**).");
        }
        return sectionListMatch(this.sections, otherPath.sections);
    };
    return Path;
}());
export default Path;
var SectionMatchResult;
(function (SectionMatchResult) {
    SectionMatchResult[SectionMatchResult["NotMatch"] = -1] = "NotMatch";
    SectionMatchResult[SectionMatchResult["Match"] = 0] = "Match";
    SectionMatchResult[SectionMatchResult["NextMatch"] = 1] = "NextMatch";
})(SectionMatchResult || (SectionMatchResult = {}));
/**
 * 路径分段匹配算法
 *
 * @param {Array<string>} left
 * @param {Array<string>} right
 * @returns {boolean} 匹配返回true, 否则返回false
 */
function sectionListMatch(left, right) {
    while (left.length && right.length) {
        var currentLeft = left.shift();
        var nextLeft = left[0]; // 如果数组里没元素则为undefined, sectionMatch会认为没有nextLeft可用
        var currentRight = right.shift();
        var sectionResult = sectionMatch(currentLeft, nextLeft, currentRight);
        if (sectionResult === SectionMatchResult.NotMatch) {
            return false;
        }
        else if (sectionResult === SectionMatchResult.Match) {
            if (currentLeft === doubleWildcard) {
                // 把通配符放回去
                left.unshift(currentLeft);
            }
        }
        else if (sectionResult === SectionMatchResult.NextMatch) {
            if (currentLeft === doubleWildcard) {
                // 此时需要要把下一个元素取出来, 下次匹配的时候直接从下下个开始
                left.shift();
            }
        }
    }
    if (right.length) {
        return false;
    }
    if (left.length && left.filter(function (el) { return el !== doubleWildcard; }).length) {
        // 如果left中剩下的不止有多段通配符, 则说明匹配失败
        return false;
    }
    return true;
}
/**
 * 单个路径分段匹配算法
 *
 * @param {string} left
 * @param {string} nextLeft
 * @param {string} right
 * @returns {SectionMatchResult} left匹配right返回Match, nextLeft匹配right返回NextMatch, 不匹配返回NotMatch
 */
function sectionMatch(left, nextLeft, right) {
    if (left === wildcard) {
        // 通配符
        return SectionMatchResult.Match;
    }
    if (left === doubleWildcard) {
        // 多分段通配符
        if (sectionMatch(nextLeft, "", right) === SectionMatchResult.Match) {
            // **/x 或 **/* 或 **/**
            return SectionMatchResult.NextMatch;
        }
        return SectionMatchResult.Match;
    }
    if (left === right) {
        return SectionMatchResult.Match;
    }
    return SectionMatchResult.NotMatch;
}
//# sourceMappingURL=data:application/json;base64,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